import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, combineLatest, map, Observable, of, startWith, switchMap, take, tap } from 'rxjs';
import {
  AnalyticsService,
  AppRoutes,
  AppsFlyerService,
  DashboardAnalyticsEvent,
  ENV_CONFIG,
  EnvConfig,
  LocationAccessService,
  LocationAnalyticsData,
  PLATFORM_TOKEN,
  PlatformProvider,
  ProfileRoutes
} from '@core';
import { getNewVersionNative } from '@update';
import {
  DashboardLocationMessageQueryHandler,
  DashboardProfileMessageQueryHandler,
  DashboardRedCardMessageQueryHandler,
  DashboardUpdateMessageQueryHandler
} from '../../../application/handlers';
import {
  DashboardMessageId,
  DashboardMessageQueryResult
} from '../../../application/models/dashboard-message.query-result';
import { DashboardMessagesStorage } from '../../../infrastructure/storage/dashboard-messages.storage';

const PROFILE_CLICK_EVENT: string = 'dashboard.info-box.profile-update';
const LOCATION_CLICK_EVENT: string = 'dashboard.info-box.location-enable';

@Injectable()
export class DashboardMessagesViewService {
  private readonly updateHandler: DashboardUpdateMessageQueryHandler = inject(DashboardUpdateMessageQueryHandler);
  private readonly profileHandler: DashboardProfileMessageQueryHandler = inject(DashboardProfileMessageQueryHandler);
  private readonly redCardHandler: DashboardRedCardMessageQueryHandler = inject(DashboardRedCardMessageQueryHandler);
  private readonly locationHandler: DashboardLocationMessageQueryHandler = inject(DashboardLocationMessageQueryHandler);

  private readonly storage: DashboardMessagesStorage = inject(DashboardMessagesStorage);
  private readonly router: Router = inject(Router);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);
  private readonly env: EnvConfig = inject(ENV_CONFIG);
  private readonly locationAccessService: LocationAccessService = inject(LocationAccessService);
  private readonly appsFlyerService: AppsFlyerService = inject(AppsFlyerService);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);

  readonly messages$: Observable<DashboardMessageQueryResult[]> = combineLatest([
    this.resilient(this.updateHandler.message()),
    this.resilient(this.profileHandler.message()),
    this.resilient(this.redCardHandler.message()),
    this.resilient(this.locationHandler.message())
  ]).pipe(
    map((messages: (DashboardMessageQueryResult | null)[]) =>
      messages
        .filter(
          (message: DashboardMessageQueryResult | null): message is DashboardMessageQueryResult => message !== null
        )
        .sort((a: DashboardMessageQueryResult, b: DashboardMessageQueryResult) => a.priority - b.priority)
    )
  );

  onClick(message: DashboardMessageQueryResult): void {
    switch (message.id) {
      case DashboardMessageId.Update:
        this.analyticsService.buttonClick(DashboardAnalyticsEvent.InfoBoxUpdate);
        getNewVersionNative(this.platformProvider, this.env);
        return;
      case DashboardMessageId.Profile:
        this.appsFlyerService.logEvent(PROFILE_CLICK_EVENT);
        this.analyticsService.buttonClick(DashboardAnalyticsEvent.InfoBoxProfile);
        this.router.navigate([AppRoutes.PROFILE, ProfileRoutes.EDIT]);
        return;
      case DashboardMessageId.Location:
        this.openLocationFlow();
        return;
      default:
        return;
    }
  }

  onDismiss(message: DashboardMessageQueryResult): void {
    if (!message.dismissToken) {
      return;
    }

    if (message.id === DashboardMessageId.Update) {
      this.analyticsService.buttonClick(DashboardAnalyticsEvent.InfoBoxUpdateDismiss);
      this.storage.dismissUpdate(message.dismissToken);
      return;
    }

    if (message.id === DashboardMessageId.RedCard) {
      this.analyticsService.buttonClick(DashboardAnalyticsEvent.InfoBoxRedCardDismiss);
      this.storage.dismissRedCard(message.dismissToken);
    }
  }

  private openLocationFlow(): void {
    this.appsFlyerService.logEvent(LOCATION_CLICK_EVENT);

    this.locationAccessService
      .accessState$()
      .pipe(
        take(1),
        tap((state: LocationAnalyticsData) =>
          this.analyticsService.buttonClick(DashboardAnalyticsEvent.InfoBoxLocation, state)
        ),
        switchMap(() => this.locationAccessService.ensureLocation$()),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private resilient(
    message$: Observable<DashboardMessageQueryResult | null>
  ): Observable<DashboardMessageQueryResult | null> {
    return message$.pipe(
      startWith(null),
      catchError(() => of(null))
    );
  }
}
